import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, X, BellRing, BellOff, Smartphone, Loader2 } from "lucide-react";
import { useInstallPrompt, subscribeToPush, unsubscribeFromPush, hasActivePushSubscription } from "../../lib/adminPwa";
import { api } from "../../lib/api";

/**
 * V25 Phase 5b — Floating PWA install + push notification banner for Admin.
 * Sits in the top-right corner of the dashboard. Auto-hides when:
 *  - App is already installed
 *  - User has dismissed
 *  - Push subscription active and install not available
 */
export default function AdminPwaInstallBanner() {
    const { canInstall, installed, install, dismiss } = useInstallPrompt();
    const [pushOn, setPushOn] = useState(null); // null = unknown
    const [busy, setBusy] = useState("");
    const [hidden, setHidden] = useState(false);
    const [pushSupported, setPushSupported] = useState(true);

    useEffect(() => {
        setPushSupported("serviceWorker" in navigator && "PushManager" in window);
        hasActivePushSubscription().then(setPushOn);
    }, []);

    if (hidden) return null;
    // Dismissed once → hard-hide across reloads (spec requirement)
    if (typeof window !== "undefined" && localStorage.getItem("at_admin_install_dismissed_v3") === "1") return null;
    // Nothing to show?
    if (installed && pushOn !== false) return null;
    if (!canInstall && pushOn !== false) return null;

    const onInstall = async () => {
        setBusy("install");
        try {
            const ok = await install();
            if (ok) toast.success("Admin app installed");
        } finally { setBusy(""); }
    };

    const onEnablePush = async () => {
        setBusy("push");
        try {
            await subscribeToPush(api);
            setPushOn(true);
            toast.success("Push notifications enabled");
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(""); }
    };

    const onDisablePush = async () => {
        setBusy("push");
        try {
            await unsubscribeFromPush(api);
            setPushOn(false);
            toast.info("Push notifications disabled");
        } catch (err) {
            toast.error(err.message);
        } finally { setBusy(""); }
    };

    return (
        <div className="fixed top-3 right-3 z-50 max-w-xs" data-testid="admin-pwa-banner">
            <div className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white rounded-2xl shadow-2xl border border-indigo-400/30 p-3 backdrop-blur">
                <div className="flex items-start gap-2">
                    <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                        <Smartphone className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.15em] opacity-80 font-bold">PWA</div>
                        <div className="font-bold text-sm leading-tight">Anand Tutorials Admin</div>
                        <div className="text-[11px] opacity-80 mt-0.5">Install for instant access &amp; alerts</div>
                    </div>
                    <button onClick={() => { setHidden(true); dismiss(); }} data-testid="admin-pwa-dismiss"
                        className="text-white/60 hover:text-white p-1 -mr-1 -mt-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                    {canInstall && !installed && (
                        <button onClick={onInstall} disabled={busy === "install"} data-testid="admin-pwa-install-btn"
                            className="col-span-2 bg-white text-indigo-700 font-bold text-xs py-2 rounded-xl inline-flex items-center justify-center gap-1 active:scale-95 transition disabled:opacity-60">
                            {busy === "install" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Install app
                        </button>
                    )}
                    {pushSupported && !pushOn && (
                        <button onClick={onEnablePush} disabled={busy === "push"} data-testid="admin-pwa-push-enable"
                            className={`${canInstall ? "col-span-2" : "col-span-2"} bg-white/15 hover:bg-white/25 text-white font-bold text-xs py-2 rounded-xl inline-flex items-center justify-center gap-1 active:scale-95 transition disabled:opacity-60`}>
                            {busy === "push" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5" />} Enable notifications
                        </button>
                    )}
                    {pushOn && (
                        <button onClick={onDisablePush} disabled={busy === "push"} data-testid="admin-pwa-push-disable"
                            className="col-span-2 bg-white/15 hover:bg-white/25 text-white font-bold text-xs py-2 rounded-xl inline-flex items-center justify-center gap-1 active:scale-95 transition disabled:opacity-60">
                            {busy === "push" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BellOff className="w-3.5 h-3.5" />} Disable notifications
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
